'use client';

import Image from 'next/image';
import { useEffect, useMemo, useRef, type RefObject } from 'react';
import { Fragment, useState } from 'react';
import classNames from 'classnames';
import { Listbox, ListboxOptions, ListboxOption } from '@headlessui/react';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { useOnClickOutside } from 'usehooks-ts';
import { useIsTabletOrDesktop } from 'app/hooks/useMediaQuery';

import IconArrow from 'app/components/sprites/IconArrow';

import 'overlayscrollbars/overlayscrollbars.css';

interface ISelectBoxOption {
  name: string;
  value: string;
  image?: string;
}

interface ISelectBoxProps {
  label?: string;
  value?: string | string[];
  required?: boolean;
  placeholder?: string;
  multiple?: boolean;
  showSelectedOptions?: boolean;
  showSelectedOptionCount?: boolean;
  options: ISelectBoxOption[];
  onChange(option: ISelectBoxOption | ISelectBoxOption[]): void;
}

export default function SelectBox({
  label,
  value,
  required,
  placeholder,
  multiple = false,
  showSelectedOptions = true,
  showSelectedOptionCount = false,
  options,
  onChange
}: ISelectBoxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const parentRef = useRef<HTMLDivElement>(null);

  const [selectedOption, setSelectedOption] = useState<
    ISelectBoxOption | ISelectBoxOption[] | null
  >(
    (!!value &&
      (Array.isArray(value)
        ? options.filter(o => value.includes(o.value))
        : options.find(o => o.value === value))) ||
      (multiple ? [] : null)
  );

  const caption = useMemo(() => {
    return showSelectedOptions
      ? (!!selectedOption &&
          (Array.isArray(selectedOption)
            ? selectedOption.map(o => o.name).join(', ')
            : selectedOption.name)) ||
          placeholder
      : placeholder;
  }, [selectedOption, placeholder, showSelectedOptions]);

  const isTabletOrDesktop = useIsTabletOrDesktop();

  function handleChange(option: ISelectBoxOption | ISelectBoxOption[]) {
    setSelectedOption(option);
    typeof onChange === 'function' && onChange(option);
  }

  function open() {
    setIsOpen(true);
  }

  function close() {
    setIsOpen(false);
  }

  function toggleOpen() {
    setIsOpen(prev => !prev);
  }

  // usehooks-ts@3.1.0 ещё ждёт RefObject<T>, а в типах React 19 useRef(null)
  // даёт RefObject<T | null> — каст снимает несоответствие.
  useOnClickOutside(parentRef as RefObject<HTMLDivElement>, () => {
    if (!isTabletOrDesktop) return;
    close();
  });

  useEffect(() => {
    setSelectedOption(
      (!!value &&
        (Array.isArray(value)
          ? options.filter(o => value.includes(o.value))
          : options.find(o => o.value === value))) ||
        (multiple ? [] : null)
    );
  }, [value, multiple, options]);

  return (
    <div ref={parentRef} className="flex flex-col gap-2.5 md:gap-3 xl:gap-4">
      {!!label && (
        <div className="flex gap-2 items-center">
          <div className="form-field-label">
            <span>{label}</span>
            {!!required && <span className="text-current">*</span>}
          </div>
        </div>
      )}
      <Listbox
        value={selectedOption ?? undefined}
        onChange={handleChange}
        by="value"
        multiple={multiple}
      >
        {({ open }) => (
          <div className="relative">
            <button
              type="button"
              onClick={toggleOpen}
              className="relative flex items-center gap-3 py-1 w-full px-4 text-h2 text-brown md:border border-brown rounded cursor-pointer"
            >
              <span className="flex items-center gap-2 leading-none flex-grow overflow-x-clip text-left whitespace-nowrap text-ellipsis">
                <span className="py-2">{caption}</span>
                {showSelectedOptionCount &&
                  !showSelectedOptions &&
                  Array.isArray(selectedOption) &&
                  !!selectedOption.length && (
                    <span className="px-2 py-1 bg-current rounded-sm min-w-6 text-center">
                      <span className="text-white">
                        {selectedOption.length}
                      </span>
                    </span>
                  )}
              </span>
              <IconArrow
                className={classNames(
                  'w-3 h-3 fill-none stroke-current',
                  isOpen ? '-rotate-90' : 'rotate-90'
                )}
              />
            </button>
            <ListboxOptions
              as="div"
              transition
              modal={false}
              unmount={false}
              static
              className={classNames(
                'relative md:absolute z-10 md:right-0 md:top-full w-full md:w-[320px] md:max-h-[320px] md:overflow-hidden md:mt-1',
                'pt-2 pb-7 md:pl-4 md:pr-5 bg-white rounded no-scrollbar md:shadow-sm',
                { hidden: !isOpen }
              )}
            >
              <OverlayScrollbarsComponent
                defer
                options={{
                  scrollbars: { autoHide: 'leave', autoHideDelay: 100 }
                }}
                className="[&_.os-scrollbar_.os-scrollbar-track]:bg-brown-20 [&_.os-scrollbar_.os-scrollbar-track]:w-[3px] [&_.os-scrollbar-handle]:bg-brown [&_.os-scrollbar-handle]:w-[3px]"
              >
                <div className="flex flex-col md:max-h-[288px] md:pr-5">
                  {options.map(option => (
                    <ListboxOption
                      key={option.value}
                      value={option}
                      as={Fragment}
                    >
                      {({ focus, selected }) => (
                        <div
                          className={classNames(
                            'flex gap-5 items-center px-4 border-b border-brown-20 cursor-pointer',
                            option.image ? 'py-1' : 'py-4'
                          )}
                        >
                          {!!option.image && (
                            <div
                              className={classNames(
                                'relative w-[46px] min-w-[46px] h-15 p-0.5 overflow-hidden rounded',
                                { 'border border-brown': selected }
                              )}
                            >
                              <Image
                                src={option.image}
                                alt={option.name}
                                fill
                                quality={100}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}
                          <div
                            className={classNames(
                              'text-h2 transition-opacity duration-300 break-words',
                              focus && !selected ? 'opacity-70' : ''
                            )}
                          >
                            {option.name}
                          </div>
                          <div
                            className={classNames(
                              'min-w-3 min-h-3 ml-auto border border-brown rounded-full',
                              { 'bg-brown': selected }
                            )}
                          />
                        </div>
                      )}
                    </ListboxOption>
                  ))}
                </div>
              </OverlayScrollbarsComponent>
            </ListboxOptions>
          </div>
        )}
      </Listbox>
    </div>
  );
}
